import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, InputAdornment, IconButton, Paper, ClickAwayListener } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, parse, isValid, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isBefore, isWithinInterval, addMonths, subMonths, startOfDay } from 'date-fns';

const DateRangePicker = ({ startDate, endDate, onChange, minDate = new Date() }) => {
  // Normalize minDate to start of day for date comparisons
  const minDateNormalized = startOfDay(minDate);
  const [startDateText, setStartDateText] = useState('');
  const [endDateText, setEndDateText] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [activeField, setActiveField] = useState(null); // 'start' or 'end'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const calendarRef = useRef(null);

  // Initialize text fields from props
  useEffect(() => {
    if (startDate && isValid(startDate)) {
      setStartDateText(format(startDate, 'MM/dd/yyyy'));
      setStartDateError(''); // Clear error when valid date is set
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate && isValid(endDate)) {
      setEndDateText(format(endDate, 'MM/dd/yyyy'));
      setEndDateError(''); // Clear error when valid date is set
    }
  }, [endDate]);

  const parseAndFormatStartDate = (value) => {
    // If empty, just return
    if (!value || value.trim() === '') {
      setStartDateError('');
      return;
    }
    
    // Try to parse the date with multiple formats
    // Try 2-digit year formats first, then 4-digit year formats
    const formats = ['MM/dd/yy', 'M/d/yy', 'MM/d/yy', 'M/dd/yy', 'MM/dd/yyyy', 'M/d/yyyy', 'MM/d/yyyy', 'M/dd/yyyy'];
    let parsedDate = null;
    
    for (const formatStr of formats) {
      // Use a reference date to help parse 2-digit years correctly (50 year window)
      const referenceDate = new Date();
      const testDate = parse(value, formatStr, referenceDate);
      if (isValid(testDate)) {
        parsedDate = testDate;
        break;
      }
    }
    
    if (parsedDate) {
      // Always format the date to MM/dd/yyyy
      const formattedStart = format(parsedDate, 'MM/dd/yyyy');
      setStartDateText(formattedStart);
      
      if (!isBefore(parsedDate, minDateNormalized)) {
        // Valid date that's not in the past
        setStartDateError('');
        
        // If there's no end date text, automatically set it to the same date as start date
        const hasEndDateText = endDateText && endDateText.trim() !== '';
        
        let newEndDate = parsedDate; // Default to start date for 1-day rental
        
        if (hasEndDateText && endDate) {
          // Check if existing end date is before the new start date
          if (isBefore(endDate, parsedDate)) {
            // End date is invalid - update it to match the new start date
            newEndDate = parsedDate;
            setEndDateText(format(parsedDate, 'MM/dd/yyyy'));
            setEndDateError('');
          } else {
            // Keep existing end date if it's still valid
            newEndDate = endDate;
          }
        } else {
          // Auto-fill end date text field with start date
          const formattedEnd = format(parsedDate, 'MM/dd/yyyy');
          setEndDateText(formattedEnd);
          setEndDateError('');
        }
        
        // Trigger onChange to update pricing
        onChange({ startDate: parsedDate, endDate: newEndDate });
      } else {
        // Date is in the past - show error but keep the formatted date
        setStartDateError('Date cannot be in the past');
      }
    } else {
      // Invalid date format - clear the field and revert to previous valid value
      if (startDate) {
        setStartDateText(format(startDate, 'MM/dd/yyyy'));
        setStartDateError('');
      } else {
        setStartDateText('');
        setStartDateError('Invalid date format');
      }
    }
  };

  const handleStartDateChange = (e) => {
    const value = e.target.value;
    setStartDateText(value);
    // Clear error while typing
    if (startDateError) {
      setStartDateError('');
    }
  };

  const handleStartDateBlur = () => {
    parseAndFormatStartDate(startDateText);
  };

  const handleStartDateKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      parseAndFormatStartDate(startDateText);
      setShowCalendar(false);
    }
  };

  const parseAndFormatEndDate = (value) => {
    // If empty, just return
    if (!value || value.trim() === '') {
      setEndDateError('');
      return;
    }
    
    // Try to parse the date with multiple formats
    // Try 2-digit year formats first, then 4-digit year formats
    const formats = ['MM/dd/yy', 'M/d/yy', 'MM/d/yy', 'M/dd/yy', 'MM/dd/yyyy', 'M/d/yyyy', 'MM/d/yyyy', 'M/dd/yyyy'];
    let parsedDate = null;
    
    for (const formatStr of formats) {
      // Use a reference date to help parse 2-digit years correctly (50 year window)
      const referenceDate = new Date();
      const testDate = parse(value, formatStr, referenceDate);
      if (isValid(testDate)) {
        parsedDate = testDate;
        break;
      }
    }
    
    if (parsedDate) {
      // Always format the date to MM/dd/yyyy
      setEndDateText(format(parsedDate, 'MM/dd/yyyy'));
      
      if (!isBefore(parsedDate, minDateNormalized)) {
        // Valid date that's not in the past
        
        // If there's a start date, validate that end date is not before it
        if (startDate) {
          if (!isBefore(parsedDate, startDate)) {
            // Valid end date - trigger onChange to update pricing
            setEndDateError('');
            onChange({ startDate, endDate: parsedDate });
          } else {
            // End date is before start date - show error but keep the formatted date
            setEndDateError('Must be after start date');
          }
        } else {
          // No start date yet, set both to the entered date
          setStartDateText(format(parsedDate, 'MM/dd/yyyy'));
          setStartDateError('');
          setEndDateError('');
          onChange({ startDate: parsedDate, endDate: parsedDate });
        }
      } else {
        // Date is in the past - show error but keep the formatted date
        setEndDateError('Date cannot be in the past');
      }
    } else {
      // Invalid date format - clear the field and revert to previous valid value
      if (endDate) {
        setEndDateText(format(endDate, 'MM/dd/yyyy'));
        setEndDateError('');
      } else {
        setEndDateText('');
        setEndDateError('Invalid date format');
      }
    }
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    setEndDateText(value);
    // Clear error while typing
    if (endDateError) {
      setEndDateError('');
    }
  };

  const handleEndDateBlur = () => {
    parseAndFormatEndDate(endDateText);
  };

  const handleEndDateKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      parseAndFormatEndDate(endDateText);
      setShowCalendar(false);
    }
  };

  const handleCalendarIconClick = (field) => {
    setActiveField(field);
    setShowCalendar(true);
    
    const today = startOfDay(new Date());
    const currentMonthStart = startOfMonth(today);
    
    if (field === 'start') {
      // For start date: show current month or the month of the selected start date
      if (startDate) {
        setCurrentMonth(startDate);
      } else {
        setCurrentMonth(today);
      }
    } else if (field === 'end') {
      // For end date: show the month after start date or the month of the selected end date
      if (endDate) {
        // If there's an end date, try to position calendar so end date is visible
        // Show the month before the end date, but never before current month
        const monthBeforeEndDate = startOfMonth(subMonths(endDate, 1));
        if (isBefore(monthBeforeEndDate, currentMonthStart)) {
          // Don't show past months - use current month instead
          setCurrentMonth(today);
        } else {
          setCurrentMonth(monthBeforeEndDate);
        }
      } else if (startDate) {
        // If there's a start date but no end date, show start month and next month
        setCurrentMonth(startDate);
      } else {
        // No dates selected, default to current month
        setCurrentMonth(today);
      }
    }
  };

  const handleDateClick = (date) => {
    if (isBefore(date, minDateNormalized)) return; // Don't allow past dates

    if (activeField === 'start') {
      setStartDateText(format(date, 'MM/dd/yyyy'));
      
      // Check if existing end date is before the new start date
      let newEndDate = date;
      if (endDate && !isBefore(endDate, date)) {
        // Keep existing end date if it's still valid (on or after new start date)
        newEndDate = endDate;
      } else {
        // End date is invalid or empty - update it to match the new start date
        setEndDateText(format(date, 'MM/dd/yyyy'));
      }
      
      onChange({ startDate: date, endDate: newEndDate });
    } else if (activeField === 'end') {
      if (startDate && isBefore(date, startDate)) {
        // If end date is before start date, swap them
        onChange({ startDate: date, endDate: startDate });
        setStartDateText(format(date, 'MM/dd/yyyy'));
        setEndDateText(format(startDate, 'MM/dd/yyyy'));
      } else {
        onChange({ startDate: startDate || date, endDate: date });
        setEndDateText(format(date, 'MM/dd/yyyy'));
      }
    }
    
    // Close the calendar after selecting a date
    setShowCalendar(false);
  };

  const renderMonth = (monthDate) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Get the day of week for the first day (0 = Sunday)
    const firstDayOfWeek = monthStart.getDay();
    
    // Create empty cells for days before the month starts
    const emptyCells = Array(firstDayOfWeek).fill(null);
    
    return (
      <Box sx={{ width: '280px', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, fontWeight: 600, fontSize: '16px' }}>
          {format(monthDate, 'MMMM yyyy')}
        </Box>
        
        {/* Day headers */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', mb: 1 }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <Box key={day} sx={{ textAlign: 'center', fontSize: '12px', fontWeight: 600, color: '#666', py: 0.5 }}>
              {day}
            </Box>
          ))}
        </Box>
        
        {/* Calendar grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {/* Empty cells for days before month starts */}
          {emptyCells.map((_, index) => (
            <Box key={`empty-${index}`} sx={{ height: '36px' }} />
          ))}
          
          {/* Actual days */}
          {(() => {
            const today = new Date();
            return daysInMonth.map(day => {
              const isStart = startDate && isSameDay(day, startDate);
              const isEnd = endDate && isSameDay(day, endDate);
              const isInRange = startDate && endDate && isWithinInterval(day, { start: startDate, end: endDate });
              const isPast = isBefore(day, minDateNormalized);
              const isToday = isSameDay(day, today);
              
              return (
              <Box
                key={day.toString()}
                onClick={() => !isPast && handleDateClick(day)}
                sx={{
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isPast ? 'not-allowed' : 'pointer',
                  borderRadius: isStart || isEnd ? '50%' : '0',
                  backgroundColor: isStart || isEnd ? '#00B894' : isInRange ? 'rgba(0,184,148,0.2)' : 'transparent',
                  color: (isStart || isEnd) ? 'white' : isPast ? '#ccc' : isToday ? '#00B894' : '#333',
                  fontWeight: (isStart || isEnd || isToday) ? 600 : 400,
                  fontSize: '14px',
                  border: isToday && !isStart && !isEnd ? '1px solid #00B894' : 'none',
                  '&:hover': isPast ? {} : {
                    backgroundColor: isStart || isEnd ? '#00A983' : 'rgba(0,184,148,0.3)',
                  },
                  transition: 'background-color 0.2s',
                  opacity: isPast ? 0.4 : 1
                }}
              >
                {format(day, 'd')}
              </Box>
            );
          });
          })()}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="Start Date"
          value={startDateText}
          onChange={handleStartDateChange}
          onBlur={handleStartDateBlur}
          onKeyDown={handleStartDateKeyDown}
          placeholder="MM/DD/YYYY"
          error={!!startDateError}
          helperText={startDateError}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: 'white',
              '&.Mui-focused fieldset': {
                borderColor: startDateError ? '#d32f2f' : '#00B894'
              }
            },
            '& .MuiFormHelperText-root': {
              color: '#d32f2f',
              marginLeft: 0,
              marginTop: '3px',
              fontSize: '0.7rem',
              lineHeight: 1.2,
              textAlign: 'center'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  onClick={() => handleCalendarIconClick('start')}
                  edge="start"
                  tabIndex={-1}
                  sx={{ color: startDateError ? '#d32f2f' : '#00B894' }}
                >
                  <CalendarMonthIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          label="End Date"
          value={endDateText}
          onChange={handleEndDateChange}
          onBlur={handleEndDateBlur}
          onKeyDown={handleEndDateKeyDown}
          placeholder="MM/DD/YYYY"
          error={!!endDateError}
          helperText={endDateError}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: 'white',
              '&.Mui-focused fieldset': {
                borderColor: endDateError ? '#d32f2f' : '#00B894'
              }
            },
            '& .MuiFormHelperText-root': {
              color: '#d32f2f',
              marginLeft: 0,
              marginTop: '3px',
              fontSize: '0.7rem',
              lineHeight: 1.2,
              textAlign: 'center'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  onClick={() => handleCalendarIconClick('end')}
                  edge="start"
                  tabIndex={-1}
                  sx={{ color: endDateError ? '#d32f2f' : '#00B894' }}
                >
                  <CalendarMonthIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {showCalendar && (
        <ClickAwayListener onClickAway={() => setShowCalendar(false)}>
          <Paper
            ref={calendarRef}
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 10000,
              mt: 1,
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} sx={{ color: '#00B894' }}>
                <ChevronLeftIcon />
              </IconButton>
              
              <Box sx={{ flex: 1 }} />
              
              <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} sx={{ color: '#00B894' }}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex' }}>
              {renderMonth(currentMonth)}
              {renderMonth(addMonths(currentMonth, 1))}
            </Box>
          </Paper>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default DateRangePicker;
